<template>
  <div class="map-location-picker">
    <div class="mb-3">
      <h5 class="mb-0">{{ label }}</h5>
      <span class="text-muted small">{{ subtitle }}</span>
    </div>

    <div class="mb-3">
      <label class="form-label">Buscar dirección</label>
      <div class="input-group">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="Escribe una dirección o colonia"
          @input="onSearchInput"
        />
      </div>
      <div v-if="suggestions.length" class="suggestions-list mt-2">
        <button
          v-for="(item, idx) in suggestions"
          :key="idx"
          type="button"
          class="suggestion-item"
          @click="selectSuggestion(item)"
        >
          <div class="fw-semibold">{{ item.text }}</div>
          <div class="small text-muted">{{ item.place_name }}</div>
        </button>
      </div>
      <div v-if="error" class="alert alert-danger py-2 mt-2">{{ error }}</div>
    </div>

    <div class="map-wrapper">
      <div ref="mapContainer" class="mapbox-container" :style="{ height: mapHeight }"></div>
      <div class="center-marker" aria-hidden="true"></div>
    </div>

    <div class="mt-3 d-flex justify-content-end">
      <button class="btn btn-success" :disabled="loadingReverse || !hasValidCoords" @click="confirm">
        {{ loadingReverse ? 'Actualizando...' : 'Confirmar' }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MapLocationPicker',
  props: {
    label: { type: String, required: true },
    subtitle: { type: String, default: '' },
    mapHeight: { type: String, default: '320px' },
    initialLatitude: { type: Number, default: null },
    initialLongitude: { type: Number, default: null },
    initialAddress: { type: String, default: '' },
    stateBbox: { type: Array, default: () => [] },
    stateCenter: { type: Array, default: () => [] },
    lockToState: { type: Boolean, default: false },
  },
  data() {
    return {
      map: null,
      searchQuery: '',
      latitudeInput: this.initialLatitude,
      longitudeInput: this.initialLongitude,
      addressInput: this.initialAddress,
      loadingSearch: false,
      loadingReverse: false,
      error: '',
      mapToken: process.env.VUE_APP_MAPBOX_TOKEN || '',
      suggestions: [],
      reverseTimer: null,
      searchDebounceTimer: null,
      searchSessionToken: '',
      detectedState: '',
      searchRequestId: 0,
    };
  },
  computed: {
    hasValidCoords() {
      const lat = Number(this.latitudeInput);
      const lng = Number(this.longitudeInput);
      return Number.isFinite(lat) && Number.isFinite(lng);
    },
  },
  watch: {
    initialLatitude(val) {
      if (val !== null && val !== undefined) this.latitudeInput = val;
      this.syncFromProps();
    },
    initialLongitude(val) {
      if (val !== null && val !== undefined) this.longitudeInput = val;
      this.syncFromProps();
    },
    initialAddress(val) {
      if (typeof val === 'string') {
        this.addressInput = val;
        this.searchQuery = val;
      }
    },
    stateBbox() {
      this.fitToState();
    },
    stateCenter() {
      this.fitToState();
    },
  },
  mounted() {
    this.searchSessionToken = this.generateSessionToken();
    this.initMap();
  },
  beforeUnmount() {
    if (this.map) this.map.remove();
    this.map = null;
    if (this.reverseTimer) {
      clearTimeout(this.reverseTimer);
      this.reverseTimer = null;
    }
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
  },
  methods: {
    initMap() {
      const mapboxgl = window.mapboxgl;
      if (!mapboxgl) {
        this.error = 'Mapbox no está cargado. Revisa la conexión a internet o el script de Mapbox.';
        return;
      }
      if (!this.mapToken) {
        this.error = 'Falta VUE_APP_MAPBOX_TOKEN en el frontend.';
        return;
      }
      mapboxgl.accessToken = this.mapToken;

      const hasInitialCoords = Number.isFinite(this.latitudeInput) && Number.isFinite(this.longitudeInput);
      const lat = hasInitialCoords ? Number(this.latitudeInput) : this.stateCenter?.[1] ?? 23.6345;
      const lng = hasInitialCoords ? Number(this.longitudeInput) : this.stateCenter?.[0] ?? -102.5528;

      this.map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [lng, lat],
        zoom: 12,
      });

      this.map.on('moveend', () => {
        const center = this.map.getCenter();
        this.latitudeInput = center.lat;
        this.longitudeInput = center.lng;
        if (this.reverseTimer) clearTimeout(this.reverseTimer);
        this.reverseTimer = setTimeout(() => {
          this.reverseGeocode(center.lat, center.lng);
        }, 250);
      });

      this.fitToState();
    },

    syncFromProps() {
      if (!this.map) return;
      const lat = Number(this.latitudeInput);
      const lng = Number(this.longitudeInput);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      this.map.setCenter([lng, lat]);
    },

    fitToState() {
      if (!this.map) return;
      if (Array.isArray(this.stateBbox) && this.stateBbox.length === 4) {
        this.map.fitBounds(this.stateBbox, { padding: 24, duration: 0 });
        return;
      }
      if (Array.isArray(this.stateCenter) && this.stateCenter.length === 2) {
        this.map.setCenter(this.stateCenter);
      }
    },

    buildGeocodingUrl(query, limit = 5) {
      const params = new URLSearchParams({
        access_token: this.mapToken,
        autocomplete: 'true',
        language: 'es',
        country: 'mx',
        limit: String(limit),
      });
      if (this.lockToState && Array.isArray(this.stateBbox) && this.stateBbox.length === 4) {
        params.set('bbox', this.stateBbox.join(','));
      }
      return `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?${params.toString()}`;
    },
    generateSessionToken() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    },
    async suggestAddressSearchBox(query, limit = 10) {
      const params = new URLSearchParams({
        q: query,
        access_token: this.mapToken,
        language: 'es',
        country: 'mx',
        limit: String(limit),
        session_token: this.searchSessionToken,
      });
      if (this.lockToState && Array.isArray(this.stateBbox) && this.stateBbox.length === 4) {
        params.set('bbox', this.stateBbox.join(','));
      }
      const url = `https://api.mapbox.com/search/searchbox/v1/suggest?${params.toString()}`;
      const resp = await fetch(url);
      if (!resp.ok) throw new Error('No se pudo consultar sugerencias');
      const data = await resp.json();
      return data?.suggestions || [];
    },
    async retrieveAddressSearchBox(mapboxId) {
      const params = new URLSearchParams({
        access_token: this.mapToken,
        session_token: this.searchSessionToken,
      });
      const url = `https://api.mapbox.com/search/searchbox/v1/retrieve/${encodeURIComponent(mapboxId)}?${params.toString()}`;
      const resp = await fetch(url);
      if (!resp.ok) throw new Error('No se pudo recuperar la dirección');
      const data = await resp.json();
      return data?.features?.[0];
    },

    onSearchInput() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.searchAddress();
      }, 450);
    },
    parseStateFromFeature(feature) {
      const context =
        feature?.properties?.context ||
        feature?.context ||
        [];
      if (Array.isArray(context)) {
        const region = context.find((c) => c?.id?.startsWith?.('region') || c?.type === 'region');
        if (region?.text || region?.name) return region.text || region.name;
      } else if (context?.region?.name) {
        return context.region.name;
      }
      const placeName = feature?.place_name || feature?.properties?.full_address || '';
      const parts = placeName.split(',').map((p) => p.trim()).filter(Boolean);
      if (parts.length >= 2) return parts[parts.length - 2];
      return '';
    },
    async searchAddress() {
      this.error = '';
      if (!this.mapToken) return;
      const q = (this.searchQuery || '').trim();
      if (q.length < 3) {
        this.suggestions = [];
        return;
      }
      const requestId = ++this.searchRequestId;
      this.loadingSearch = true;
      try {
        const suggestions = await this.suggestAddressSearchBox(q, 10);
        if (requestId !== this.searchRequestId) return;
        this.suggestions = suggestions.map((s) => ({
          text: s.name || s.place_formatted || s.full_address || 'Dirección',
          place_name: s.full_address || s.place_formatted || s.name || '',
          mapbox_id: s.mapbox_id,
        }));
        if (!this.suggestions.length) {
          this.error = 'No se encontraron resultados para esa dirección.';
        }
      } catch {
        if (requestId !== this.searchRequestId) return;
        this.error = 'Error al buscar la dirección con Mapbox.';
      } finally {
        if (requestId === this.searchRequestId) {
          this.loadingSearch = false;
        }
      }
    },

    async selectSuggestion(feature) {
      if (!feature?.mapbox_id) return;
      const retrieved = await this.retrieveAddressSearchBox(feature.mapbox_id);
      const center = retrieved?.geometry?.coordinates;
      if (!Array.isArray(center) || center.length < 2) return;
      const [lng, lat] = center;
      this.latitudeInput = lat;
      this.longitudeInput = lng;
      this.addressInput =
        retrieved?.properties?.full_address ||
        retrieved?.place_name ||
        feature.place_name ||
        `${Number(lat).toFixed(10)}, ${Number(lng).toFixed(10)}`;
      this.searchQuery = this.addressInput;
      this.detectedState = this.parseStateFromFeature(retrieved || feature);
      this.map.flyTo({ center: [lng, lat], zoom: 15 });
      this.suggestions = [];
      this.searchSessionToken = this.generateSessionToken();
    },

    async reverseGeocode(lat, lng) {
      this.error = '';
      if (!this.mapToken) return;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      this.loadingReverse = true;
      try {
        const params = new URLSearchParams({
          access_token: this.mapToken,
          types: 'address',
          country: 'mx',
          limit: '1',
        });
        if (this.lockToState && Array.isArray(this.stateBbox) && this.stateBbox.length === 4) {
          params.set('bbox', this.stateBbox.join(','));
        }
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?${params.toString()}`;
        const resp = await fetch(url);
        const data = await resp.json();
        const feature = data?.features?.[0];
        if (feature?.place_name) {
          this.addressInput = feature.place_name;
          this.searchQuery = feature.place_name;
          this.detectedState = this.parseStateFromFeature(feature);
          return;
        }
        this.addressInput = `${Number(lat).toFixed(10)}, ${Number(lng).toFixed(10)}`;
        this.searchQuery = this.addressInput;
        this.detectedState = '';
      } catch {
        if (!this.addressInput || !this.addressInput.trim()) {
          this.addressInput = `${Number(lat).toFixed(10)}, ${Number(lng).toFixed(10)}`;
          this.searchQuery = this.addressInput;
        }
        this.detectedState = '';
      } finally {
        this.loadingReverse = false;
      }
    },

    async confirm() {
      this.error = '';
      if (!this.hasValidCoords) {
        this.error = 'Debes proporcionar coordenadas válidas.';
        return;
      }
      this.$emit('confirm', {
        latitude: Number(this.latitudeInput),
        longitude: Number(this.longitudeInput),
        address: (this.addressInput || '').trim(),
        state: this.detectedState || null,
      });
    },
  },
};
</script>

<style scoped>
.map-wrapper {
  position: relative;
}
.mapbox-container {
  width: 100%;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.center-marker {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 24px;
  height: 24px;
  transform: translate(-50%, -100%);
  pointer-events: none;
  border-radius: 50% 50% 50% 0;
  background: #0d6efd;
  transform-origin: center;
  rotate: -45deg;
  box-shadow: 0 2px 8px rgba(13, 110, 253, 0.35);
}
.center-marker::after {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  top: 7px;
  left: 7px;
  border-radius: 50%;
  background: #fff;
}
.suggestions-list {
  max-height: 210px;
  overflow: auto;
  border: 1px solid #e7e7e7;
  border-radius: 10px;
  background: #fff;
}
.suggestion-item {
  width: 100%;
  text-align: left;
  border: 0;
  background: #fff;
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
}
.suggestion-item:hover {
  background: #f7fbff;
}
.suggestion-item:last-child {
  border-bottom: 0;
}
</style>

